/**
 * Route-level permission guard.
 *
 * Usage:
 *   <Route path="/security/admins" element={
 *     <RequirePermission permission="admin.list">
 *       <AdminsListPage />
 *     </RequirePermission>
 *   } />
 *
 * Single-permission form gates on that key. Array form is ANY-of (at
 * least one is enough). For ALL-of semantics nest two
 * <RequirePermission> elements.
 *
 * On deny: renders the optional `fallback` if provided, otherwise an
 * inline "Access denied" message that includes the missing permission
 * key — helpful when an admin asks why they can't see something.
 *
 * Spec: docs/superpowers/specs/2026-05-28-admin-rbac-design.md §8.1.
 */
import { type ReactNode } from 'react';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';

interface Props {
  permission: string | string[];
  children: ReactNode;
  fallback?: ReactNode;
}

export function RequirePermission({ permission, children, fallback }: Props) {
  const { loaded, hasPermission } = useAdminPermissions();

  // Don't flash "Access denied" while the initial /me fetch is in
  // flight — render nothing so the page mounts cleanly when permission
  // resolves.
  if (!loaded) return null;

  const required = Array.isArray(permission) ? permission : [permission];
  const ok = required.some(p => hasPermission(p));

  if (ok) return <>{children}</>;
  if (fallback !== undefined) return <>{fallback}</>;

  return (
    <div className="rounded-lg border bg-card p-8 text-center">
      <h2 className="text-xl font-semibold mb-2">Access denied</h2>
      <p className="text-muted-foreground">
        You don't have permission to view this page.
      </p>
      <p className="text-xs text-muted-foreground mt-4">
        Required permission: <code>{required.join(' OR ')}</code>
      </p>
      <p className="text-xs text-muted-foreground mt-2">
        Contact a Super Admin if you need access.
      </p>
    </div>
  );
}
